import React, {useEffect, useMemo, useState} from 'react';
import {Select} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Switch} from 'src/design/atoms/Switch';

import type {CourseDefinitionContentResponse} from 'src/api/ProactApiClient';
import {useGetSkillListQuery} from 'src/redux/queries/skill';
import type {IFormDataV2} from 'src/types/FormV2';
import {DemonstrationForm} from '../courseDefinition/DemonstrationForm';
import {DemonstrationFormFailableStatus} from '../courseDefinition/DemonstrationFormFailableStatus';

export interface EditContentDefinitionDemonstrationProps {
  content: CourseDefinitionContentResponse;
  onContentChange: (content: CourseDefinitionContentResponse) => void;
}

// Wave 6: a linked Skill replaces the legacy Form Builder/"Assigned Form"
// sign-off path. `skillId` is not a `DemonstrationFormMeta` field — it's
// managed here, outside the generic form engine, so selecting it can
// immediately hide the legacy Form field (the engine has no per-field
// conditional-visibility support keyed off another field's live value).
const EditContentDefinitionDemonstration: React.FC<
  EditContentDefinitionDemonstrationProps
> = ({content, onContentChange}) => {
  const [data, setData] = useState(content.demonstration as IFormDataV2);

  useEffect(() => {
    setData(content.demonstration as IFormDataV2);
  }, [content.demonstration]);

  const {data: skills} = useGetSkillListQuery({start: 0, length: 1000});
  const skillOptions = (skills?.data ?? [])
    .filter(skill => skill.active)
    .map(skill => ({value: String(skill.id), label: skill.name}));

  const skillId = content.demonstration?.skillId;
  const enableFailOnNotYetCompetent =
    content.demonstration?.enableFailOnNotYetCompetent ?? false;

  const handleFormDataChange = (formData: IFormDataV2) => {
    onContentChange({
      ...content,
      demonstration: {
        ...content.demonstration,
        headerText: formData.headerText,
        bodyText: formData.bodyText,
        assignToUserId: formData.assignToUserId,
        assignToUserGroupId: formData.assignToUserGroupId,
        // Skill-based sign-off owns the legacy Form field once a skill is
        // linked - never persist a stale form selection alongside it.
        demonstrationFormDefinitionId: skillId
          ? undefined
          : formData.demonstrationFormDefinitionId,
        // Version is undefiend so that form "floats" instead of tying to specific version.
        demonstrationFormDefinitionVersion: undefined,
        skillId,
        enableFailOnNotYetCompetent,
      },
    });
  };

  const handleSkillChange = (value: string | null) => {
    const nextSkillId = value ? Number(value) : undefined;
    onContentChange({
      ...content,
      demonstration: {
        ...content.demonstration,
        skillId: nextSkillId,
        demonstrationFormDefinitionId: nextSkillId
          ? undefined
          : content.demonstration?.demonstrationFormDefinitionId,
        // Skill-only flag: clearing the skill returns to the legacy form
        // path, which owns its own failable mechanism.
        enableFailOnNotYetCompetent: nextSkillId
          ? (content.demonstration?.enableFailOnNotYetCompetent ?? false)
          : false,
      },
    });
  };

  const handleFailOnNotYetCompetentChange = (checked: boolean) => {
    onContentChange({
      ...content,
      demonstration: {
        ...content.demonstration,
        enableFailOnNotYetCompetent: checked,
      },
    });
  };

  const demonstrationFormDefinitionId = data?.demonstrationFormDefinitionId
    ? Number(data.demonstrationFormDefinitionId)
    : undefined;

  return useMemo(
    () => (
      <>
        <Select
          label="Skill (sign-off)"
          description="Skill-based sign-off is the preferred mechanism for a practical demonstration. Leave blank to keep the legacy form-based path below."
          placeholder="No skill selected (using legacy form)"
          clearable
          searchable
          data={skillOptions}
          value={skillId ? String(skillId) : null}
          onChange={handleSkillChange}
          mb="md"
        />
        {skillId && (
          <Switch
            label="Failed skill demonstrations fail the course"
            description="When on, a Not Yet Competent result fails the course, and the course attempt limits apply. When off, the trainee gets a new sign-off request and the course stays open."
            checked={enableFailOnNotYetCompetent}
            onChange={event =>
              handleFailOnNotYetCompetentChange(event.currentTarget.checked)
            }
            mb="md"
          />
        )}
        {!skillId && (
          <Alert color="blue" mb="md">
            No skill linked yet. This demonstration uses the legacy Assigned
            Forms path below. New demonstrations should link a Skill instead.
          </Alert>
        )}
        {!skillId && (
          <DemonstrationFormFailableStatus
            formDefinitionId={demonstrationFormDefinitionId}
          />
        )}
        <DemonstrationForm
          data={data}
          mode="edit"
          hideFormSelect={Boolean(skillId)}
          onFormDataChange={handleFormDataChange}
        />
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      data,
      demonstrationFormDefinitionId,
      skillId,
      skillOptions,
      enableFailOnNotYetCompetent,
    ]
  );
};

export default EditContentDefinitionDemonstration;
